import {
  useCarRunnerLeaderboardQuery,
  type CarRunnerLeaderboardResponse,
} from "@/common/api/car-runner-score/use-car-runner-leaderboard.query";
import { getFullUserName } from "@/common/helpers/get-full-user-name";
import { cn } from "@/common/helpers/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useId, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

type LeaderboardEntry = CarRunnerLeaderboardResponse["entries"][number];

function LeaderboardRow({ entry, isOwn }: { entry: LeaderboardEntry; isOwn: boolean }): ReactElement {
  const { t } = useTranslation("common");
  const name = getFullUserName({ user: entry }) || t("Unknown");

  return (
    <li
      aria-current={isOwn || undefined}
      className={cn("flex items-baseline gap-3 py-0.5", isOwn ? "text-primary font-bold" : "text-foreground/90")}
    >
      <span className="text-muted-foreground w-7 shrink-0 text-right tabular-nums">{entry.rank}.</span>
      <span className="min-w-0 flex-1 truncate text-left">
        {name}
        {isOwn && <span className="text-muted-foreground font-normal"> ({t("RunnerLeaderboardYou")})</span>}
      </span>
      <span className="tabular-nums">{String(entry.score).padStart(5, "0")}</span>
    </li>
  );
}

export function CarRunnerLeaderboard(): ReactElement {
  const { t } = useTranslation("common");
  const titleId = useId();
  const { data, isPending, isError } = useCarRunnerLeaderboardQuery();

  const renderContent = () => {
    if (isPending) {
      return (
        <div className="space-y-1.5 py-0.5">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-4 w-full" />
          ))}
        </div>
      );
    }
    if (isError) {
      return <p className="text-muted-foreground py-1">{t("RunnerLeaderboardError")}</p>;
    }
    if (data.entries.length === 0) {
      return <p className="text-muted-foreground py-1">{t("RunnerLeaderboardEmpty")}</p>;
    }

    const ownUuid = data.own?.userUuid;
    const isOwnListed = data.entries.some((entry) => entry.userUuid === ownUuid);
    return (
      <ol>
        {data.entries.map((entry) => (
          <LeaderboardRow key={entry.userUuid} entry={entry} isOwn={entry.userUuid === ownUuid} />
        ))}
        {data.own && !isOwnListed && (
          <>
            <li aria-hidden="true" className="text-muted-foreground text-center leading-none">
              ⋮
            </li>
            <LeaderboardRow entry={data.own} isOwn />
          </>
        )}
      </ol>
    );
  };

  return (
    <section aria-labelledby={titleId} className="mt-2 w-full max-w-sm font-mono text-xs">
      <div className="mb-1.5 flex items-baseline justify-between gap-2 border-b pb-1">
        <h3 id={titleId} className="font-bold tracking-widest uppercase">
          {t("RunnerLeaderboardTitle")}
        </h3>
        <span className="text-muted-foreground text-[10px] tracking-wider uppercase">
          {t("RunnerLeaderboardDevOnly")}
        </span>
      </div>
      {renderContent()}
    </section>
  );
}
